import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { CalendarCheck, ArrowRight, Video, Headset, Sparkles, MapPin } from "lucide-react";
import { trackConversion } from "@/lib/analytics";

const EASE = [0.22, 0.61, 0.36, 1];
const SCENE_MS = 4200;

const SCENES = [
  {
    img: "https://images.unsplash.com/photo-1515189850361-04a30e8d70e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    place: "Banff, Alberta",
    kicker: "Programme 2027",
    title: (
      <>
        <span className="mr-3">🇨🇦</span>CANADA <span className="text-gold">2027</span>
      </>
    ),
    sub: "Une nouvelle année, de nouvelles opportunités de départ.",
  },
  {
    img: "https://images.unsplash.com/photo-1583813611063-aa59412bbbcf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    place: "Toronto, Ontario",
    kicker: "C'est officiel",
    title: (
      <>
        LES CANDIDATURES SONT <span className="text-gold">OUVERTES</span>
      </>
    ),
    sub: "Les places partent vite : mieux vaut préparer son dossier en avance.",
  },
  {
    img: "https://images.unsplash.com/photo-1553855804-5ccc88ae0a2b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    place: "Vancouver, Colombie-Britannique",
    kicker: "Anticipez",
    title: (
      <>
        Un départ <span className="text-gold">préparé sereinement</span>
      </>
    ),
    sub: "Préparez dès maintenant votre projet de départ pour le Canada en 2027 avec Work Travel Expert.",
  },
  {
    img: "https://images.unsplash.com/photo-1691169555002-19e569af033b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    place: "Rocheuses canadiennes",
    kicker: "Offert",
    icon: Video,
    title: (
      <>
        Consultation <span className="text-gold">GRATUITE</span> de 30 minutes
      </>
    ),
    sub: "En visio avec un conseiller, sans engagement.",
  },
  {
    img: "https://images.unsplash.com/photo-1660143158587-bddffa026e06?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
    place: "Votre projet, accompagné",
    kicker: "Un conseiller dédié",
    icon: Headset,
    title: <>Vous êtes accompagné à chaque étape</>,
    sub: "Réservez votre créneau en moins de deux minutes.",
  },
];

export const Canada2027Announcement = () => {
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setI((v) => (v + 1) % SCENES.length), SCENE_MS);
    return () => clearInterval(id);
  }, [reduce]);

  const s = SCENES[i];
  const SceneIcon = s.icon;

  return (
    <section data-testid="canada-2027-announcement" className="relative w-full overflow-hidden bg-navy">
      {/* Visuels animés */}
      <AnimatePresence initial={false}>
        <motion.img
          key={s.img}
          src={s.img}
          alt={`Canada — ${s.place}`}
          loading="eager"
          decoding="async"
          initial={{ opacity: 0, scale: reduce ? 1 : 1.03 }}
          animate={{ opacity: 1, scale: reduce ? 1 : 1.12 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.3, ease: "easeInOut" }, scale: { duration: 9, ease: "linear" } }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </AnimatePresence>
      <div className="hidden">{SCENES.map((x) => <img key={x.img} src={x.img} alt="" aria-hidden="true" />)}</div>

      <div className="absolute inset-0 bg-navy/74" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/70 to-navy/30" />
      <div className="pointer-events-none absolute -top-32 -right-16 h-[32rem] w-[32rem] rounded-full bg-gold/12 blur-[120px] animate-float-slow" />
      <div className="pointer-events-none absolute inset-x-3 top-3 bottom-3 border border-gold/20 sm:inset-x-6 sm:top-6 sm:bottom-6" />

      <div className="relative z-10 mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20 lg:py-24">
        {/* Ruban permanent */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">
            <Sparkles size={13} /> Candidatures ouvertes
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.07] px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] text-white/80 backdrop-blur-sm">
            <MapPin size={12} className="text-gold" /> {s.place}
          </span>
        </div>

        {/* Scène animée */}
        <div className="mt-7 min-h-[210px] sm:min-h-[220px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.75, ease: EASE }}
            >
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold">
                {SceneIcon ? <SceneIcon size={14} /> : null} {s.kicker}
              </span>
              <h2
                data-testid="canada-2027-title"
                className="mt-3 max-w-3xl font-heading text-3xl font-medium leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
              >
                {s.title}
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-lg">{s.sub}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* CTA permanent */}
        <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <Link
            to="/rendez-vous"
            data-testid="canada-2027-cta"
            onClick={() =>
              trackConversion("appointment", {
                ga4Event: "select_promotion",
                params: { promotion_name: "Canada 2027", creative_slot: "home_hero_banner" },
              })
            }
            className="group inline-flex items-center gap-3 rounded-full bg-gold px-7 py-4 text-xs font-bold uppercase tracking-[0.1em] text-navy shadow-2xl shadow-gold/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover hover:shadow-gold/50 sm:px-10 sm:py-5 sm:text-sm"
          >
            <CalendarCheck size={19} /> Réserver ma consultation gratuite
            <ArrowRight size={17} className="transition-transform group-hover:translate-x-1.5" />
          </Link>
          <span className="inline-flex items-center gap-2 text-xs text-white/70">
            <Video size={14} className="text-gold" /> 30 minutes en visio · sans engagement
          </span>
        </div>

        {/* Offre de préinscription */}
        <Link
          to="/preinscription-canada-2027"
          data-testid="canada-2027-prereg-cta"
          className="group mt-5 inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl border border-gold/40 bg-white/[0.07] px-5 py-4 backdrop-blur-sm transition-colors hover:border-gold hover:bg-white/[0.12]"
        >
          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gold">
            <Sparkles size={13} /> Préinscription 2027
          </span>
          <span className="text-sm text-white/90">
            Réservez votre place prioritaire — <strong className="text-white">gratuit</strong>, ou acompte 49 € déduit de votre pack
          </span>
          <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.14em] text-gold">
            En profiter <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </span>
        </Link>

        {/* Progression des scènes */}
        <div className="mt-10 flex items-center gap-2" role="tablist" aria-label="Séquence de l'annonce">
          {SCENES.map((sc, idx) => (
            <button
              key={sc.img}
              type="button"
              role="tab"
              aria-selected={idx === i}
              aria-label={`Scène ${idx + 1}`}
              onClick={() => setI(idx)}
              data-testid={`canada-2027-dot-${idx}`}
              className={`h-[3px] rounded-full transition-all duration-500 ${idx === i ? "w-12 bg-gold" : "w-6 bg-white/25 hover:bg-white/50"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
